import { AI } from "@/lib/ai/ai"
import { ImageInfoRecord } from "@/lib/catalog/types"
import { StyleRecord } from "@/storage/indexeddb"

async function loadBitmapFromUrl(url: string): Promise<ImageBitmap> {
	const res = await fetch(url)
	if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`)
	const blob = await res.blob()
	return createImageBitmap(blob)
}

// Resolve packaged sample inputs under top-level `assets/sample-inputs/*`
const sampleInputModules: Record<string, string> = import.meta.glob(
	"@assets/sample-inputs/*.{jpg,jpeg,png}",
	{ query: "?url", import: "default", eager: true },
)

// Resolve pre-computed sample image infos
const sampleImageInfosModule: Record<string, string> = import.meta.glob(
	"@assets/sample-inputs/sample-image-infos.pstyle",
	{ query: "?url", import: "default", eager: true },
)

export async function loadSampleBitmaps(): Promise<ImageBitmap[]> {
	const urls = Object.entries(sampleInputModules)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([, url]) => url)
	if (urls.length === 0) {
		return []
	}
	const bitmaps = await Promise.all(urls.map(loadBitmapFromUrl))
	return bitmaps
}

/**
 * Load pre-computed sample image infos from assets
 */
export async function loadSampleImageInfos(): Promise<Record<string, ImageInfoRecord>> {
	const urls = Object.values(sampleImageInfosModule)
	if (urls.length === 0) {
		console.warn(
			"No pre-computed sample image infos found, falling back to runtime computation",
		)
		return {}
	}

	try {
		const url = urls[0] // Should only be one file
		if (!url) {
			throw new Error("No sample image infos file found")
		}

		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`Failed to fetch sample image infos: ${response.status}`)
		}

		const arrayBuffer = await response.arrayBuffer()
		const { decodeStyle } = await import("@/lib/catalog/style-codec")
		const dummyStyle = decodeStyle(arrayBuffer)

		return dummyStyle.referenceImages
	} catch (error) {
		console.error("Failed to load pre-computed sample image infos:", error)
		return {}
	}
}

export async function generateSampleImageInfos() {
	const bitmaps = await loadSampleBitmaps()
	const sampleImageInfos: Record<string, ImageInfoRecord> = {}

	// Get sorted URLs with filenames for consistent naming
	const sortedUrls = Object.entries(sampleInputModules)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([path, url]) => ({
			path,
			url,
			filename:
				path
					.split("/")
					.pop()
					?.replace(/\.[^/.]+$/, "") || "unknown", // Remove extension
		}))

	for (let i = 0; i < bitmaps.length; i++) {
		const bitmap = bitmaps[i]
		if (!bitmap) continue

		const urlInfo = sortedUrls[i]
		const imageId = `sample-input-${urlInfo?.filename || i + 1}`
		console.log(
			`Processing sample ${i + 1}/${bitmaps.length}: ${imageId} (from ${urlInfo?.filename})`,
		)

		const imageInfo = await AI.extractImageInfoFromRefJpeg(bitmap, imageId)
		sampleImageInfos[imageId] = imageInfo
		bitmap.close()
	}

	return sampleImageInfos
}

/**
 * ONE-TIME SETUP: Generate and save pre-computed sample image infos
 * Run this once to create the sample-image-infos.pstyle file
 */
export async function generateSampleImageInfosAndSave(): Promise<void> {
	console.log("Generating pre-computed sample image infos...")

	const sampleImageInfos = await generateSampleImageInfos()

	// Create a dummy style to hold the sample image infos
	const dummyStyle: StyleRecord = {
		id: "sample-image-infos",
		projectId: "dummy",
		createdAt: new Date(),
		updatedAt: new Date(),
		name: "Sample Image Infos",
		schemaVersion: 0,
		version: 0,
		presets: {},
		referenceImages: sampleImageInfos,
		referenceCount: Object.keys(sampleImageInfos).length,
		originalId: "sample-image-infos",
		originalCreatedAt: new Date(),
	}

	// Encode the style
	const { encodeStyle } = await import("@/lib/catalog/style-codec")
	const encodedData = encodeStyle(dummyStyle)

	// Create download link
	const blob = new Blob([encodedData as unknown as BlobPart], {
		type: "application/octet-stream",
	})
	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = "sample-image-infos.pstyle"
	link.click()
	URL.revokeObjectURL(url)

	console.log("Sample image infos generated! Save the downloaded file to assets/sample-inputs/")
	console.log("Generated infos for:", Object.keys(sampleImageInfos))
}
